"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { ProseLink } from "@/components/ProseLink";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

const trackSchema = z.object({
  code: z
    .string()
    .min(1, "Please enter your order code")
    .transform((s) => s.trim().toUpperCase())
    .refine((s) => /^FM-[0-9A-Z]{3,12}$/.test(s), "Please enter a valid order code"),
  email: z
    .string()
    .min(1, "Please enter your email address")
    .email("Please enter a valid email address")
    .transform((s) => s.trim().toLowerCase()),
});

type TrackFormValues = z.infer<typeof trackSchema>;

type PageProps = { params?: Promise<Record<string, string | string[]>>; searchParams?: Promise<Record<string, string | string[] | undefined>> };

export default function TrackPage(_props: PageProps) {
  const router = useRouter();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<TrackFormValues>({
    resolver: zodResolver(trackSchema),
    defaultValues: { code: "", email: "" },
  });

  async function onSubmit(data: TrackFormValues) {
    setSubmitError(null);
    // ⚠ The `fetch` itself must be caught, not just the `.json()`. It was not:
    // offline (or any DNS/TLS failure) rejected here, react-hook-form's
    // `handleSubmit` reset `isSubmitting` in its own `finally` and RE-THREW, so
    // the customer saw the button un-press and absolutely nothing else — no
    // message, no retry cue — while an unhandled rejection went to the console.
    // A dead button with no explanation is the worst degradation available.
    let res: Response;
    try {
      res = await fetch("/api/orders/verify-access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: data.code, email: data.email }),
        credentials: "include",
      });
    } catch {
      setSubmitError(
        "We couldn't reach the server. Please check your connection and try again."
      );
      return;
    }
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) {
      setSubmitError(json.error ?? "Verification failed. Please try again.");
      return;
    }
    router.push(`/order/${data.code}`);
  }

  return (
    <div className="px-6 py-8 md:px-10 md:py-12 lg:px-16">
      <div className="mx-auto max-w-md">
        <h1 className="heading-page">
          Track your order
        </h1>
        <p className="mt-2 text-muted-foreground">
          Enter your order code and email to view status and details.
        </p>

        <Card className="mt-8 border-border/15">
          <CardHeader>
            <h2 className="font-medium text-foreground">Order code & email</h2>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <Label htmlFor="code">Order code</Label>
                <Input
                  id="code"
                  type="text"
                  className="mt-1.5 font-mono uppercase"
                  autoComplete="off"
                  placeholder="FM-ABC123"
                  aria-describedby={errors.code ? "code-error" : undefined}
                  {...register("code", {
                    onChange: (e) => setValue("code", e.target.value.toUpperCase(), { shouldValidate: true }),
                  })}
                />
                {errors.code && (
                  <p id="code-error" className="mt-1 text-sm text-destructive" role="alert">{errors.code.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  className="mt-1.5"
                  autoComplete="email"
                  aria-describedby={errors.email ? "email-error" : undefined}
                  {...register("email")}
                />
                {errors.email && (
                  <p id="email-error" className="mt-1 text-sm text-destructive" role="alert">{errors.email.message}</p>
                )}
              </div>
              {submitError && (
                <p className="text-sm text-destructive" role="alert" aria-live="polite">{submitError}</p>
              )}
              <Button
                type="submit"
                size="lg"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying&hellip;
                  </>
                ) : (
                  "Track order"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Don&apos;t have your code?{" "}
          <ProseLink href="/orders">
            Find orders by email
          </ProseLink>
        </p>
      </div>
    </div>
  );
}
